import React from 'react'
import { Box, Typography, Skeleton, useTheme, alpha } from '@mui/material'
import { useTranslation } from 'react-i18next'

const RetentionHeatmap = ({ data, loading }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: '8px' }} />
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {t('analytics_no_retention_data')}
        </Typography>
      </Box>
    )
  }

  const maxWeeks = Math.max(...data.map(c => c.retention.length))
  const weekHeaders = Array.from({ length: maxWeeks }, (_, i) => `${t('analytics_week_prefix')}${i}`)

  const getCellColor = (pct) => {
    if (pct === null || pct === undefined) return 'transparent'
    if (pct >= 80) return alpha('#22c55e', 0.7)
    if (pct >= 60) return alpha('#22c55e', 0.45)
    if (pct >= 40) return alpha('#eab308', 0.45)
    if (pct >= 20) return alpha('#f97316', 0.4)
    if (pct > 0) return alpha('#ef4444', 0.35)
    return alpha(theme.palette.text.disabled, 0.08)
  }

  const getCellText = (pct) => {
    if (pct === null || pct === undefined) return ''
    return `${pct}%`
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 600 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          <Box sx={{ width: 100, flexShrink: 0, pr: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('analytics_cohort')}
            </Typography>
          </Box>
          <Box sx={{ width: 52, flexShrink: 0, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.secondary }}>
              {t('analytics_size')}
            </Typography>
          </Box>
          {weekHeaders.map(w => (
            <Box key={w} sx={{ width: 48, flexShrink: 0, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.text.secondary }}>
                {w}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Data rows */}
        {data.map((cohort) => (
          <Box key={cohort.cohort_week} sx={{ display: 'flex', mb: '2px' }}>
            <Box sx={{ width: 100, flexShrink: 0, pr: 1, display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: theme.palette.text.primary }}>
                {cohort.cohort_week}
              </Typography>
            </Box>
            <Box sx={{ width: 52, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: theme.palette.text.secondary }}>
                {cohort.cohort_size}
              </Typography>
            </Box>
            {weekHeaders.map((_, wi) => {
              const pct = cohort.retention[wi]
              return (
                <Box
                  key={wi}
                  sx={{
                    width: 48,
                    height: 28,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: getCellColor(pct),
                    borderRadius: '4px',
                    mx: '1px',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      transform: 'scale(1.08)',
                      zIndex: 1,
                    },
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.66rem',
                    fontWeight: 600,
                    color: pct > 0 ? theme.palette.text.primary : theme.palette.text.disabled,
                  }}>
                    {getCellText(pct)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default RetentionHeatmap
